import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DialogAddCommentData } from 'src/app/components/dialogs/add-comment/models/dialog-add-comment-data';
import { CommentInput } from 'src/app/models/server/comment-input';
import { Contact } from 'src/app/models/server/contact';
import { Folder } from 'src/app/models/server/folder';
import { FolderDocument } from 'src/app/models/server/folder-document';
import { FolderWorkflowStepsSnapshot } from 'src/app/models/server/folder-workflow-steps-snapshot';
import { History } from 'src/app/models/server/history';
import { Reminder } from 'src/app/models/server/reminder';
import { StandByDatas } from 'src/app/models/server/stand-by-datas';

@Injectable()
export abstract class DetailBaseDatasource {
    /**
     * Supprime le dossier spécifié.
     */
    public abstract deleteFolder(folder: Folder): void;

    /**
     * Place le dossier spécifié en veille.
     */
    public abstract setFolderInStandByState(folder: Folder, data: StandByDatas): void;

    /**
     * Sort le dossier spécifié de la veille
     */
    public abstract removeFolderFromStandByState(folder: Folder): void;

    /**
     * Place le dossier spécifié en 'attente d'archivage'.
     */
    public abstract setFolderInWaitingArchivingState(folder: Folder): void;

    /**
     * Retire le dossier spécifié de l'état en 'attente d'archivage'.
     */
    public abstract removeFolderFromWaitingArchivingState(folder: Folder): void;

    /**
     * Liste les documents d'un dossier.
     * @param viewId
     * @param folderId
     */
    public abstract listDocuments(viewId: number, folderId: string): Observable<FolderDocument[]>;

    /**
     * Supprime le document spécifié.
     */
    public abstract deleteDocument(viewId: number, folderId: string, documentId: string): Observable<boolean>;

    /**
     * Ajoute un commentaire au dossier.
     */
    public abstract addComment(viewId: number, folderId: string, comment: DialogAddCommentData): Observable<FolderDocument>;

    /**
     * Met à jour un commentaire d'un dossier.
     */
    public abstract updateComment(viewId: number, folderId: string, comment: CommentInput): Observable<FolderDocument>;

    /**
     * Supprime un commentaire d'un dossier.
     * @param viewId
     * @param reminder
     */
    public abstract deleteComment(viewId: number, folderId: string, comment: CommentInput): Observable<FolderDocument>;

    /**
     * Ajoute un rappel au dossier.
     */
    public abstract addReminder(viewId: number, folderId: string, data: Reminder): Observable<Reminder>;

    /**
     * Liste les rappels d'un dossier.
     * @param viewId
     * @param folderId
     */
    public abstract listReminders(viewId: number, folderId: string): Observable<Reminder[]>;

    /**
     * Liste les entrées de l'historique d'un dossier.
     * @param viewId
     * @param folderId
     */
    public abstract listHistories(viewId: number, folderId: string): Observable<History[]>;

    /**
     * Met à jour un rappel d'un dossier.
     * @param viewId
     * @param folderId
     * @param data
     * @returns
     */
    public abstract updateReminder(viewId: number, folderId: string, data: Reminder): Observable<Reminder>;

    /**
     * Supprime un rappel d'un dossier.
     * @param viewId
     * @param reminder
     */
    public abstract deleteReminder(viewId: number, reminder: Reminder): Observable<boolean>;

    /**
     * Marque un document comme 'lu' ou comme 'non lu'.
     * @param viewId
     * @param folderId
     * @param documentId
     * @param isRead
     */
    public abstract markDocumentAsRead(viewId: number, folderId: string, documentId: string, isRead: boolean): Observable<FolderDocument>;

    /**
     * Marque un dossier comme 'lu' ou comme 'non lu'.
     * @param folder
     * @param isRead
     */
    public abstract markFolderAsRead(folder: Folder, isRead: boolean);

    /**
     * Ajoute un contact au carnet d'adresses.
     * @param contact
     */
    public abstract addContact(contact: Contact);

    /**
     * Déplace un dossier dans une bannette.
     * @param viewId
     * @param folder
     * @param boxId
     */
    public abstract moveFolderToBox(viewId: number, folder: Folder, boxId: string);

    /**
     * Met à jour un attribut d'un dossier.
     * @param viewId
     * @param folder
     * @param attributeId
     * @param oldValue
     * @param newValue
     */
    public abstract updateFolderAttribute(viewId: number, folder: Folder, attributeId: number, oldValue: any, newValue: any);

    /**
     * Met à jour l'état d'une étape du workflow.
     * @param viewId
     * @param folderId
     * @param workflowStepsSnapshot
     * @param newStateId
     */
    public abstract updateFolderWorkflowStepState(viewId: number, folderId: string, workflowStepsSnapshot: FolderWorkflowStepsSnapshot, newStateId: number);

    /**
     * Active une étape de workflow d'un dossier.
     * @param workflowStepsSnapshot : Instance de l'étape du workflow.
     */
    public abstract activateFolderWorkflowStep(viewId: number, folderId: string, workflowsStepId: number): void;

    /**
     * Finalise une étape de workflow d'un dossier.
     * @param viewId
     * @param folderId
     * @param workflowStepsSnapshot
     */
    public abstract completeFolderWorkflowStep(viewId: number, folderId: string, workflowStepsSnapshot: FolderWorkflowStepsSnapshot): void;

    /**
     * Archive le dossier.
     * @param folder Instance du dossier.
     */
    public abstract archive(folder: Folder): void;

    /**
     * Suit le dossier.
     */
    public abstract followFolder(folder: Folder): void;

    /**
     * Suit le dossier.
     */
    public abstract stopFollowFolder(folder: Folder): void;
}
